import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useDebouncedValue } from '@tyro/core/hooks';
import { Scrollbar } from '@tyro/core/scrollbar';
import { SearchInput } from '@tyro/core/search-input';
import { useTranslation } from '@tyro/i18n';
import { CloseIcon } from '@tyro/icons';
import { useReaderActions } from '../../store/documentation-reader-provider';
import { ReaderHome } from './reader-home';
import { SearchResultsPanel } from './search-results-panel';

const MIN_SEARCH_LENGTH = 2;

// The whole "home" region. Search is local state here — it never reaches the
// provider, so typing only re-renders this subtree, and "search" is no longer
// a navigation view. Owns its own pinned bar + scroll area because the search
// box must stay fixed above the (scrolling) results/home content.
export function ReaderSearch() {
  const { t } = useTranslation(['documentation', 'common']);
  const { navigateToSpace, navigateToPage } = useReaderActions();
  const {
    value: searchValue,
    debouncedValue: debouncedSearch,
    setValue: setSearchValue,
  } = useDebouncedValue<string>({ defaultValue: '', delay: 300 });

  const isSearching = debouncedSearch.length >= MIN_SEARCH_LENGTH;

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 'solid 1px',
          borderBottomColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <SearchInput
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          placeholder={t('documentation:reader.searchPlaceholder')}
          endAdornment={
            searchValue ? (
              <IconButton
                size="small"
                aria-label={t('common:actions.clear')}
                onClick={() => setSearchValue('')}
                sx={{ mr: -0.5 }}
              >
                <CloseIcon sx={{ width: 18, height: 18 }} />
              </IconButton>
            ) : null
          }
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Scrollbar style={{ height: '100%' }}>
          {isSearching ? (
            <Box sx={{ px: 3, py: 2.5 }}>
              <SearchResultsPanel
                query={debouncedSearch}
                onSelectSpace={navigateToSpace}
                onNavigatePage={navigateToPage}
              />
            </Box>
          ) : (
            <ReaderHome />
          )}
        </Scrollbar>
      </Box>
    </>
  );
}
